import clsx from "clsx";
import React from "react";
import { sort } from "radash";
import { Icon } from "@nubeteck/icons";
import { Card } from "@nubeteck/prime";
import { useGlobalQuery } from "@hooks";
import { Message } from "primereact/message";
import { StrategicReviewTypes } from "@core";
import { Timeline } from "primereact/timeline";
import { Datejs, JsxUtil } from "@nubeteck/utils";
import { IStrategicPlansReview } from "@interfaces";
import { ProgressSpinner } from "primereact/progressspinner";

import { EmptyMessage } from "../empty-message";

export interface IRequestCommentsCardProps {
  strategicPlanId: number;
}

const RequestCommentsCard = ({
  strategicPlanId,
}: IRequestCommentsCardProps) => {
  const reviews = useGlobalQuery(
    "StrategicPlans",
    "getAllReviews",
    strategicPlanId,
    { enabled: !!strategicPlanId },
  );

  const customizedContent = (item: IStrategicPlansReview) => {
    const isTypeApproved =
      item.TipoRevisionId === StrategicReviewTypes.APPROVED;

    return (
      <div className="flex flex-column mb-5">
        <div className="flex justify-content-between align-items-center gap-2 mb-3 font-medium">
          <span className="line-height-1">
            {Datejs(item.FechaCreacion).format("LLLL")}
          </span>
          <span className="line-height-1">
            Revisado por:{" "}
            <b className="text-primary-600 cursor-pointer">
              {item.CreadoPorNombre}
            </b>
          </span>
        </div>
        <Message
          className="justify-content-start"
          severity={isTypeApproved ? "success" : "error"}
          text={
            <span>
              <b>{item.TipoRevisionNombre}:</b>{" "}
              {item.Descripcion || "Sin observación"}
            </span>
          }
          icon={
            <Icon
              className="mr-2"
              touchable={false}
              name={isTypeApproved ? "eye-check" : "eye-x"}
            />
          }
        />
      </div>
    );
  };

  const customizedMarker = (item: IStrategicPlansReview) => (
    <div className="py-1">
      <div
        className={clsx(
          "p-1 border-circle",
          item.TipoRevisionId === StrategicReviewTypes.APPROVED
            ? "bg-primary"
            : "bg-red-500",
        )}
      />
    </div>
  );

  return (
    <Card
      title="Comentarios de revisión"
      actions={[
        <Icon
          size={20}
          name="refresh"
          key="RefreshButton"
          className="text-primary"
          onClick={() => reviews.refetch()}
        />,
      ]}
    >
      {JsxUtil.renderLoader(
        reviews.isFetching,
        <div className="h-12rem flex justify-content-center align-items-center">
          <ProgressSpinner
            strokeWidth="5"
            animationDuration="1s"
            className="w-full w-3rem h-3rem"
          />
        </div>,
      )(
        <div className="flex justify-content-center">
          {reviews.data?.length ? (
            <Timeline
              align="alternate"
              marker={customizedMarker}
              content={customizedContent}
              value={sort(
                reviews.data ?? [],
                (item) => Datejs(item.FechaCreacion).unix(),
                true,
              )}
            />
          ) : (
            <EmptyMessage message="No hay comentarios de revision" />
          )}
        </div>,
      )}
    </Card>
  );
};

export default RequestCommentsCard;
